import { useMemo, useState } from 'react';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useDebounce } from '@app/utils';
import { criteriaQuestionsKeys } from '@data/constants';
import { APIError, OrderDirection } from '@data/index';
import {
  AnswerType,
  GetQuestionsBody,
  QuestionCountListView,
  QuestionOrderBy,
  QuestionsApiGetQuestionsRequest,
  QuestionType,
} from '@data/sdk/match';
import { MatchingApi } from '@data/utils/axios-instance';

export const useQuestionsList = (answerTypes?: AnswerType[]) => {
  const [query, setQuery] = useState<GetQuestionsBody>({
    limit: 10,
    offset: 0,
    searchString: '',
    questionTypes: [QuestionType.Standard],
    orderBy: QuestionOrderBy.UpdatedAt,
    orderDirection: OrderDirection.DESC,
    currentVersionOnly: false,
  });

  const debouncedSearch = useDebounce(query.searchString, 500);
  const { data, fetchNextPage, hasNextPage, isLoading, isFetching } = useInfiniteQuery<
    QuestionCountListView,
    APIError
  >({
    queryKey: criteriaQuestionsKeys.list({
      getQuestionsBody: { ...query, searchString: debouncedSearch, answerTypes },
    }),
    initialPageParam: {
      getQuestionsBody: {
        ...query,
        searchString: debouncedSearch,
        ...(answerTypes?.length && { answerTypes }),
      },
    },
    queryFn: async ({ pageParam }) => {
      const response = await MatchingApi.questions.getQuestions(
        pageParam as QuestionsApiGetQuestionsRequest,
      );
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextOffset = allPages.reduce((prev, next) => prev + next.items.length, 0);

      return nextOffset < lastPage.count
        ? {
            limit: query.limit,
            offset: nextOffset,
            searchString: debouncedSearch,
            questionTypes: query.questionTypes,
            ...(answerTypes?.length && { answerTypes }),
          }
        : undefined;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });

  const questions = data?.pages.flatMap((page) => page.items) || [];

  const questionOptions = useMemo(
    () =>
      questions.map((question) => ({
        label: question.text,
        value: question.id,
        answerType: question.answerType,
      })),
    [questions],
  );

  const loadMoreQuestions = () => {
    fetchNextPage();
  };

  const handleSearchChange = (v?: string) => {
    setQuery({ ...query, searchString: v || undefined, offset: 0 });
  };

  return {
    questions,
    questionOptions,
    loadMoreQuestions,
    hasMoreQuestions: hasNextPage,
    questionsLoading: isLoading || isFetching,
    query,
    setQuery,
    handleSearchChange,
  };
};
